import { 
  collection, 
  doc, 
  getDoc,
  getDocs, 
  setDoc,
  updateDoc, 
  deleteDoc, 
  query, 
  orderBy,
  onSnapshot,
  serverTimestamp
} from 'firebase/firestore';
import { db } from './config';

const COLLECTION_NAME = 'users';

/**
 * Get user by ID (includes profile + permissions)
 */
export const getUserById = async (userId) => {
  try {
    const userDoc = await getDoc(doc(db, COLLECTION_NAME, userId));
    if (userDoc.exists()) {
      return { success: true, data: { id: userDoc.id, ...userDoc.data() } };
    }
    return { success: false, error: 'User not found' };
  } catch (error) {
    console.error('Error getting user:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get all users
 */
export const getAllUsers = async () => {
  try {
    const q = query(collection(db, COLLECTION_NAME), orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    const users = [];
    querySnapshot.forEach((doc) => {
      users.push({ id: doc.id, ...doc.data() });
    });
    return { success: true, data: users };
  } catch (error) {
    console.error('Error getting users:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Subscribe to users in real-time
 */
export const subscribeToUsers = (callback) => {
  try {
    const q = query(collection(db, COLLECTION_NAME), orderBy('createdAt', 'desc'));
    return onSnapshot(q, (snapshot) => {
      const users = [];
      snapshot.forEach((doc) => {
        users.push({ id: doc.id, ...doc.data() });
      });
      callback(users);
    }, (error) => {
      console.error('Error subscribing to users:', error);
      callback([]);
    });
  } catch (error) {
    console.error('Error setting up users subscription:', error);
    return () => {};
  }
};

/**
 * Create new user (profile + permissions)
 * Use Firebase Auth UID as document ID
 */
export const createUser = async (userId, userData) => {
  try {
    const userDoc = {
      // Profile info
      email: userData.email,
      displayName: userData.displayName || userData.name || '',
      name: userData.name || userData.displayName || '',
      phone: userData.phone || '',
      role: userData.role || 'Staff',
      status: userData.status || 'Active',
      
      // Permissions
      userType: userData.userType || 'staff',
      permissions: userData.permissions || getDefaultPermissions(userData.userType || 'staff'),
      
      // Metadata
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      createdBy: userData.createdBy || '',
      disabled: false,
    };

    // Use Auth UID as document ID
    await setDoc(doc(db, COLLECTION_NAME, userId), userDoc);
    
    return { success: true, id: userId };
  } catch (error) {
    console.error('Error creating user:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Update user profile
 */
export const updateUser = async (userId, userData) => {
  try {
    const userRef = doc(db, COLLECTION_NAME, userId);
    await updateDoc(userRef, {
      ...userData,
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    console.error('Error updating user:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Update user permissions
 */
export const updateUserPermissions = async (userId, permissions) => {
  try {
    const userRef = doc(db, COLLECTION_NAME, userId);
    await updateDoc(userRef, {
      permissions,
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    console.error('Error updating permissions:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Update user type (admin/staff)
 */
export const updateUserType = async (userId, userType) => {
  try {
    const userRef = doc(db, COLLECTION_NAME, userId);
    const permissions = userType === 'admin' ? getAdminPermissions() : getDefaultPermissions('staff');
    
    await updateDoc(userRef, {
      userType,
      permissions,
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    console.error('Error updating user type:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Disable/Enable user
 */
export const setUserStatus = async (userId, disabled) => {
  try {
    const userRef = doc(db, COLLECTION_NAME, userId);
    await updateDoc(userRef, {
      disabled,
      status: disabled ? 'Inactive' : 'Active',
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    console.error('Error setting user status:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Delete user
 */
export const deleteUser = async (userId) => {
  try {
    await deleteDoc(doc(db, COLLECTION_NAME, userId));
    return { success: true };
  } catch (error) {
    console.error('Error deleting user:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get user permissions
 */
export const getUserPermissions = async (userId) => {
  try {
    const userDoc = await getDoc(doc(db, COLLECTION_NAME, userId));
    if (userDoc.exists()) {
      const userData = userDoc.data();
      return { 
        success: true, 
        data: {
          userType: userData.userType || 'staff',
          ...userData.permissions
        }
      };
    }
    // Return default staff permissions if not found
    return { 
      success: true, 
      data: {
        userType: 'staff',
        ...getDefaultPermissions('staff')
      }
    };
  } catch (error) {
    console.error('Error getting user permissions:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get default permissions based on user type
 */
export const getDefaultPermissions = (userType) => {
  if (userType === 'admin') {
    return getAdminPermissions();
  }

  // Staff must NEVER get profit/margin-related access by default —
  // viewProfitLoss and monthlyPnL stay false here regardless of what else changes.
  return {
    dashboard: true,
    inventory: false,
    expenses: false,
    reports: false,
    branches: false,
    staff: false,
    settings: false,
    modules: false,
    viewProfitLoss: false,
    monthlyPnL: false,
    deleteExpenses: false,
  };
};

/**
 * Get admin permissions (all enabled)
 */
export const getAdminPermissions = () => {
  return {
    dashboard: true,
    inventory: true,
    expenses: true,
    reports: true,
    branches: true,
    staff: true,
    settings: true,
    modules: true,
    viewProfitLoss: true,
    monthlyPnL: true,
    deleteExpenses: true,
  };
};

/**
 * Permissions that reveal profit, margin, or cost data — staff accounts must
 * never have these set to true, no matter what an admin toggles in Module Access.
 */
export const PROFIT_SENSITIVE_PERMISSIONS = ['viewProfitLoss', 'monthlyPnL', 'reports'];

/**
 * Strip profit-sensitive flags from a permissions object for non-admin users.
 * Used as a last-resort guard when saving permissions from the Module Access screen.
 */
export const sanitizePermissionsForUserType = (permissions, userType) => {
  if (userType === 'admin') return permissions;
  const sanitized = { ...permissions };
  PROFIT_SENSITIVE_PERMISSIONS.forEach((key) => { sanitized[key] = false; });
  return sanitized;
};

/**
 * Check if user has permission for a module
 */
export const hasPermission = (userData, module) => {
  if (!userData) return false;
  if (userData.userType === 'admin') return true;
  return userData.permissions?.[module] === true;
};

/**
 * Migrate old data structure to new one
 * This helps transition from staff + permissions collections to single users collection
 */
export const migrateUserData = async (userId, staffData, permissionsData) => {
  try {
    const userData = {
      // From staff collection
      email: staffData.email,
      displayName: staffData.name,
      name: staffData.name,
      phone: staffData.phone || '',
      role: staffData.role || 'Staff',
      status: staffData.status || 'Active',
      
      // From permissions collection
      userType: permissionsData.userType || 'staff',
      permissions: {
        dashboard: permissionsData.dashboard || false,
        grn: permissionsData.grn || false,
        stockTransfer: permissionsData.stockTransfer || false,
        inventory: permissionsData.inventory || false,
        stockHistory: permissionsData.stockHistory || false,
        billing: permissionsData.billing || false,
        returns: permissionsData.returns || false,
        customers: permissionsData.customers || false,
        creditors: permissionsData.creditors || false,
        expenses: permissionsData.expenses || false,
        reports: permissionsData.reports || false,
        sales: permissionsData.sales || false,
        staff: permissionsData.staff || false,
        activityLogs: permissionsData.activityLogs || false,
        settings: permissionsData.settings || false,
        modules: permissionsData.modules || false,
        viewProfitLoss: permissionsData.viewProfitLoss || false,
        deleteBilling: permissionsData.deleteBilling || false,
        deleteInventory: permissionsData.deleteInventory || false,
        deleteGRN: permissionsData.deleteGRN || false,
        deleteExpenses: permissionsData.deleteExpenses || false,
        deleteCustomers: permissionsData.deleteCustomers || false,
      },
      
      // Metadata
      createdAt: staffData.createdAt || serverTimestamp(),
      updatedAt: serverTimestamp(),
      disabled: false,
    };

    await setDoc(doc(db, COLLECTION_NAME, userId), userData);
    return { success: true };
  } catch (error) {
    console.error('Error migrating user data:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Ensure all users have userType field - migration for existing users
 */
export const ensureUserTypesExist = async () => {
  try {
    const q = query(collection(db, COLLECTION_NAME), orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    let migratedCount = 0;

    for (const userDoc of querySnapshot.docs) {
      const userData = userDoc.data();
      
      // If userType is missing, add it
      if (!userData.userType) {
        await updateDoc(doc(db, COLLECTION_NAME, userDoc.id), {
          userType: 'staff',
          updatedAt: serverTimestamp()
        });
        migratedCount++;
      }
    }
    
    console.log(`Migrated ${migratedCount} users to have userType field`);
    return { success: true, migratedCount };
  } catch (error) {
    console.error('Error ensuring userTypes exist:', error);
    return { success: false, error: error.message };
  }
};
